import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { RiEdit2Line } from "@remixicon/react";
import {
    Form as ApiForm,
    Chat,
    Ticket,
    TicketInput,
} from "../../../../../__generated__/api";
import Form from "../../../../form/Form";
import UserDisplay from "../../../labels/user/User";
import Styles from "../TicketDetails.module.scss";
import {
    DetailRow,
    DetailSection,
    TicketProperties, sectionRegistry,
} from "./base";
import ActionExecute from "../../../../action-execute/ActionExecute";
import { STATUS_ID } from "../../../../../contexts/ticket-state/ticket-item";

export const SUBJECT_ID = "urn:fq:form-field:subject";
export const DESCRIPTION_ID = "urn:fq:form-field:description";
export const PRIORITY_ID = "urn:fq:form-field:priority";
export const REQUESTOR_ID = "urn:fq:form-field:requestorId";
export const TAGS_ID = "urn:fq:form-field:tags";

export default function RequestSection({
    ticket, isMinimized, onToggleExpand, isReadOnly, metricPathPrefix,
}: TicketProperties) {
    const { isFirstLoading } = ticket.useChatDetails({ isTicketOnly: isReadOnly });

    const [action, setAction] = useState<ApiForm>();

    const updateTicket = async (input: Partial<TicketInput>) => {
        ticket.saveTicket(input);
    };

    const handleChangeRequestType = (oldForm: ApiForm | undefined, newForm: ApiForm) => {
        if (oldForm?.id !== newForm.id && newForm.hasAutomation) {
            setAction(newForm);
        }
    };

    const handleChangeRequester = useCallback((userId: string) => {
        if (userId !== ticket.requester.id) {
            updateTicket({
                requestorId: userId,
            });
        }
    }, [ticket]);

    return (
        <DetailSection
            title="Request"
            isMinimized={isMinimized}
            onToggleExpand={onToggleExpand}
        >
            {action && (
                <ActionExecute
                    actionId={action.id}
                    chat={ticket.chat as Chat}
                    chatId={ticket.id}
                    onHide={() => setAction(undefined)}
                    onEmptyMessage=""
                    shouldSaveForm
                >
                    <div className="mb-3">
                        <div className="alert alert-info">
                            <h5 className="d-block">Automations Detected</h5>
                            <span>
                                The request type &quot;
                                {action.name}
                                &quot;
                                has some automations which
                                would run when the ticket is created.
                                Do you want to execute them now?
                            </span>
                        </div>
                        {action.fields.isNotEmpty && (
                            <>
                                <strong className="mt-4 d-block">Ticket Overrides</strong>
                                <small>
                                    Here are the fields used by &quot;
                                    {action.name}
                                    &quot;.
                                    Override them below and click execute to fire the automations.
                                </small>
                            </>
                        )}

                    </div>
                </ActionExecute>
            )}
            <DetailRow title="Requester">
                <UserDisplay
                    user={ticket.requester}
                    onChange={!isReadOnly ? handleChangeRequester : undefined}
                    allowOverlay={!isReadOnly}
                    metricPathPrefix={[...metricPathPrefix, "requester"]}
                />
            </DetailRow>
            <Form
                variant="horizontal"
                ticket={ticket.ticket as Ticket}
                isEditable={!isReadOnly}
                className={classNames(Styles["details-section-body"], isFirstLoading ? "loading" : "")}
                canSelectRequestType
                shouldValidateAutomations
                onChange={updateTicket}
                onChangeRequestType={handleChangeRequestType}
                shouldValidateAutomation
                editIcon={<RiEdit2Line className="form-edit-icon" />}
                metricPathPrefix={[...metricPathPrefix, "request"]}
                skipFields={[
                    PRIORITY_ID,
                    REQUESTOR_ID,
                    TAGS_ID,
                    STATUS_ID,
                ]}
            />

        </DetailSection>

    );
}

sectionRegistry.register({
    sectionId: "urn:fq:portal-chat-section:request",
    label: "Request",
    component: RequestSection,
});
